import {
  BookingRecordDetailsCustomer,
  BookingRecordListForCustomer,
  BookingStatus,
  LicensePlate,
  OrderPaymentStatus,
  OrderRecordBase,
  OrderSettlementKind,
  PaymentOption,
  PhoneNumberCountry
} from '@navio/nsdk';
import groupBy from 'lodash/groupBy';
import map from 'lodash/map';
import {
  InsuranceInfoForCar,
  SelectedSalesItemsCar,
  SelectedSalesItemsForCar,
  ServiceCategoryFlatType
} from 'noddi-types';
import { NoddiDateFormats, noddiFormatDate } from '../date/date';

export const UPCOMING_BOOKING_STATUSES: BookingStatus[] = [BookingStatus.CONFIRMED, BookingStatus.ASSIGNED_TO_ROUTE];

export const COMPLETED_BOOKING_STATUSES: BookingStatus[] = [BookingStatus.COMPLETED];

export const CANCELLED_BOOKING_STATUSES: BookingStatus[] = [BookingStatus.CANCELLED];

/**
 * Unconfirmed bookings holding their slot until the customer pays.
 *
 * Two states, not interchangeable: `DRAFT_PAY_BEFORE` is a customer's own
 * checkout, transient and hidden from staff; `ADMIN_CREATED_PAYMENT_REQUESTED`
 * is one staff created for a customer and must be surfaced so it can be chased.
 * Use this set where the distinction does not matter — anything about money
 * still being owed before the booking counts.
 */
const AWAITING_PAYMENT_BOOKING_STATUSES: BookingStatus[] = [
  BookingStatus.DRAFT_PAY_BEFORE,
  BookingStatus.ADMIN_CREATED_PAYMENT_REQUESTED
];

/** Whether the booking is still waiting on a customer payment to be confirmed. */
export const getIsAwaitingCustomerPayment = (status: BookingStatus): boolean =>
  AWAITING_PAYMENT_BOOKING_STATUSES.includes(status);

/**
 * Whether a status-only booking PATCH may move `from` → `to`. Mirrors the
 * backend guards in `update_booking`: pay-before targets need payment details a
 * status-only change does not send, and CONFIRMED is reachable only from DRAFT
 * or a pay-before status.
 */
export const getIsStatusOnlyBookingChangeAllowed = ({ from, to }: { from: BookingStatus; to: BookingStatus }) => {
  if (to === from) {
    return true;
  }
  if (from === BookingStatus.CANCELLED) {
    return false;
  }
  if (getIsAwaitingCustomerPayment(to)) {
    return false;
  }
  return to !== BookingStatus.CONFIRMED || from === BookingStatus.DRAFT || getIsAwaitingCustomerPayment(from);
};

export const groupCars = (
  items: SelectedSalesItemsCar[]
): Array<{
  licensePlate: LicensePlate;
  selectedSalesItemIds: number[];
}> => {
  // Create composite key for grouping
  const grouped = groupBy(items, (item) => `${item.licensePlate.number}_${item.licensePlate.countryCode}`);

  return map(grouped, (group) => ({
    licensePlate: {
      number: group[0]!.licensePlate.number,
      countryCode: group[0]!.licensePlate.countryCode as PhoneNumberCountry,
      personalisedNumber: group[0]!.licensePlate.personalisedNumber ?? null
    },
    selectedSalesItemIds: group.map((item) => item.salesItemId)
  }));
};

export const groupCarsWithQuantity = (items: SelectedSalesItemsCar[]): SelectedSalesItemsForCar[] => {
  // Create composite key for grouping
  const grouped = groupBy(items, (item) => `${item.licensePlate.number}_${item.licensePlate.countryCode}`);

  return map(grouped, (group) => ({
    licensePlate: {
      number: group[0]!.licensePlate.number,
      countryCode: group[0]!.licensePlate.countryCode
    },
    salesItems: group.map((item) => ({
      salesItemId: item.salesItemId,
      quantity: 1
    }))
  }));
};

export const compareLicensePlates = (licensePlate1: LicensePlate, licensePlate2: LicensePlate) =>
  licensePlate1.number === licensePlate2.number && licensePlate1.countryCode === licensePlate2.countryCode;

export const getSalesItemCarsPayload = (
  selectedSalesItemsCars: SelectedSalesItemsCar[],
  insuranceInfo?: InsuranceInfoForCar[]
) =>
  groupCars(selectedSalesItemsCars).map((car) => {
    const carInsuranceInfo = insuranceInfo?.find((info) => compareLicensePlates(info.licensePlate, car.licensePlate));
    const tireHotelItem = selectedSalesItemsCars.find(
      (item) =>
        compareLicensePlates(item.licensePlate, car.licensePlate) &&
        (item.metaData?.selectedTireHotelPickUpAddress || item.metaData?.selectedTireHotelLocation)
    );

    const incomingTireSeason = selectedSalesItemsCars.find(
      (item) => compareLicensePlates(item.licensePlate, car.licensePlate) && item.metaData?.incomingTireSeason
    )?.metaData?.incomingTireSeason;

    const addressPickupId = tireHotelItem?.metaData?.selectedTireHotelPickUpAddress?.id;
    const tireHotelLocationId = tireHotelItem?.metaData?.selectedTireHotelLocation?.id;

    return {
      licensePlate: {
        ...car.licensePlate,
        countryCode: car.licensePlate.countryCode as PhoneNumberCountry
      },
      selectedSalesItemIds: car.selectedSalesItemIds,
      wheelPickup:
        addressPickupId || tireHotelLocationId
          ? {
              addressPickupId,
              tireHotelLocationId,
              ...tireHotelItem?.metaData?.tireHotelPickupDecision
            }
          : null,
      incomingTireSeason,
      insuranceInfo: carInsuranceInfo
        ? {
            ...carInsuranceInfo,
            dateOfDamage: carInsuranceInfo.dateOfDamage
              ? noddiFormatDate(carInsuranceInfo.dateOfDamage, NoddiDateFormats.API_DATE)
              : undefined
          }
        : undefined
    };
  });

export const getShouldPayForBooking = (order: OrderRecordBase) => {
  return (
    order.amountGross.amount > 0 &&
    order.amountOutstanding.amount > 0 &&
    !order.isManuallyInvoiced &&
    order.paymentOption !== PaymentOption.INVOICE &&
    order.settlementKind !== OrderSettlementKind.EXTERNAL
  );
};

/**
 * Shop collected the order and Navio did not. Partner uses this for its
 * mark. Admin shows the API label on the orders table. The customer app
 * does not show a mark: it only uses this so a visit that has not happened
 * yet stays on the prepaid sentence.
 *
 * When `paymentStatus` is present it is the only definition: a leftover
 * EXTERNAL settlement plus a real Navio capture is PAID, not paid outside.
 * List rows omit the status, so they use a positive, fully paid external
 * settlement. A zero-gross lock and a shell hold (nothing paid) do not.
 */
export const isMarkedPaidOutsideApp = (order: {
  paymentStatus?: { value: OrderPaymentStatus } | null;
  settlementKind: OrderSettlementKind;
  amountGross: { amount: number };
  amountPaid: { amount: number };
  isFullyPaid: boolean;
}): boolean => {
  if (order.paymentStatus != null) {
    return order.paymentStatus.value === OrderPaymentStatus.PAID_EXTERNAL;
  }
  return (
    order.settlementKind === OrderSettlementKind.EXTERNAL &&
    order.amountGross.amount > 0 &&
    order.amountPaid.amount > 0 &&
    order.isFullyPaid
  );
};

export const getShouldGiveFeedback = (booking: BookingRecordDetailsCustomer): boolean => {
  return Boolean(
    booking.completedAt && !booking.isFullyUnableToComplete && !booking.serviceFeedback && booking.order.isFullyPaid
  );
};

export const getIsUpcomingBooking = (booking: BookingRecordListForCustomer): boolean => {
  return !booking.completedAt && !booking.isFullyUnableToComplete && !booking.isCancelled;
};

/** Prefer typeObj (non-deprecated); fallback to type for backward compatibility. */
const getServiceCategoryTypeValue = (category: {
  type?: { value: number } | null;
  typeObj?: { value: number } | null;
}): number | undefined => category.typeObj?.value ?? category.type?.value;

export const getIsWindShieldRepairJob = (booking: BookingRecordDetailsCustomer): boolean => {
  return booking.serviceCategories.some(
    (category) => getServiceCategoryTypeValue(category) === ServiceCategoryFlatType.WINDSCREEN_REPAIR
  );
};

/** True when every service category is windscreen repair (uses typeObj, not deprecated type). */
export const getIsOnlyWindShieldRepairJob = (booking: BookingRecordDetailsCustomer): boolean => {
  return (
    booking.serviceCategories.length > 0 &&
    booking.serviceCategories.every(
      (category) => getServiceCategoryTypeValue(category) === ServiceCategoryFlatType.WINDSCREEN_REPAIR
    )
  );
};

export const getIsTireServiceJob = (booking: BookingRecordDetailsCustomer): boolean => {
  return booking.serviceCategories.some((category) => {
    const v = getServiceCategoryTypeValue(category);
    return v === ServiceCategoryFlatType.WHEEL_CHANGE || v === ServiceCategoryFlatType.WHEEL_STORAGE;
  });
};

/**
 * Service done and the order still owes money, so payment can be started.
 *
 * Keyed on COMPLETED rather than `completedAt` because status is what the
 * backend gates on: pay-by-slug accepts the awaiting-payment drafts and
 * COMPLETED, never CONFIRMED. The two are not interchangeable — a status
 * update can land without stamping the timestamp, and a booking cancelled
 * after service keeps a timestamp the backend will refuse to charge against.
 */
export const getJobCompletedAndPaymentLeft = (booking: BookingRecordDetailsCustomer): boolean => {
  const isCompleted = booking.status.value === BookingStatus.COMPLETED;

  return isCompleted && !booking.order.isFullyPaid && getShouldPayForBooking(booking.order);
};

/**
 * Pay-before order the customer settled upfront, so nothing is charged at or
 * after the service. Outstanding below zero (a refund due) still counts: the
 * backend's `isFullyPaid` wants exactly zero. A free order is not "paid".
 */
export const getIsPaidInAdvance = (order: OrderRecordBase): boolean =>
  order.settlementKind !== OrderSettlementKind.EXTERNAL &&
  order.paymentOption === PaymentOption.PAY_BEFORE &&
  order.amountOutstanding.amount <= 0 &&
  order.amountPaid.amount > 0;

/**
 * Pay-after booking that still owes money and has not been carried out yet.
 *
 * The mirror of `getJobCompletedAndPaymentLeft` over the same status policy:
 * these are the statuses the service has yet to happen in, so the page
 * explains the wait rather than offering payment that cannot start.
 */
export const getIsPayAfterAwaitingCompletion = (booking: BookingRecordDetailsCustomer): boolean => {
  const isAwaitingService = UPCOMING_BOOKING_STATUSES.includes(booking.status.value);

  return (
    isAwaitingService &&
    booking.order.paymentOption === PaymentOption.PAY_AFTER &&
    getShouldPayForBooking(booking.order)
  );
};
